/**
 * FilterChips — pill row for `event_source`, `event_type`, and status
 * (active/dismissed) filters. Designed to fit above the month grid
 * on narrow viewports (wraps naturally).
 *
 * Source axis: Grant + Manual are selectable; Mortgage / Funding /
 * Credit ship as visibly greyed-out chips with a factual tooltip
 * ("Grant events only for now") per user brief. Clicking a greyed
 * chip is a no-op — never toggles state.
 */
import { SOURCE_META, TYPE_META, STATUS_META } from "./utils";

function Chip({
  label,
  active,
  disabled,
  onClick,
  testid,
  tooltip,
  colorAccent,
}) {
  const bg = active
    ? colorAccent || "rgba(167,139,250,0.14)"
    : "rgba(148,163,184,0.06)";
  const border = active
    ? colorAccent
      ? colorAccent.replace(/0\.\d+/, "0.45")
      : "rgba(167,139,250,0.45)"
    : "var(--v2-border)";
  const color = active
    ? colorAccent
      ? colorAccent.replace(/(rgba?\()([^,]+),\s*([^,]+),\s*([^,]+).*/, "rgb($2,$3,$4)")
      : "var(--v2-accent)"
    : disabled
      ? "rgba(148,163,184,0.55)"
      : "var(--v2-textMuted)";
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={tooltip}
      data-testid={testid}
      data-active={active ? "true" : "false"}
      data-disabled={disabled ? "true" : "false"}
      className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-semibold whitespace-nowrap transition-colors"
      style={{
        background: bg,
        border: `1px solid ${border}`,
        color,
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {label}
    </button>
  );
}

export default function FilterChips({
  sources,
  onToggleSource,
  types,
  onToggleType,
  includeDismissed,
  onToggleDismissed,
}) {
  return (
    <div
      data-testid="calendar-filter-chips"
      className="flex items-center gap-2 flex-wrap"
    >
      <span className="text-[10px] uppercase tracking-[0.18em] text-v2-textMuted mr-1">
        Source
      </span>
      {Object.entries(SOURCE_META).map(([key, meta]) => {
        const isActive = sources.includes(key);
        return (
          <Chip
            key={`source-${key}`}
            testid={`calendar-filter-source-${key}`}
            label={meta.label}
            tooltip={meta.tooltip}
            active={meta.active && isActive}
            disabled={!meta.active}
            onClick={() => onToggleSource(key)}
          />
        );
      })}
      <span className="mx-2 h-4 w-px bg-v2-border" aria-hidden />
      <span className="text-[10px] uppercase tracking-[0.18em] text-v2-textMuted mr-1">
        Type
      </span>
      {Object.entries(TYPE_META).map(([key, meta]) => {
        const isActive = types.includes(key);
        return (
          <Chip
            key={`type-${key}`}
            testid={`calendar-filter-type-${key}`}
            label={meta.label}
            tooltip={meta.tooltip}
            active={isActive}
            colorAccent={meta.chipBg}
            onClick={() => onToggleType(key)}
          />
        );
      })}
      <span className="mx-2 h-4 w-px bg-v2-border" aria-hidden />
      <Chip
        testid="calendar-filter-include-dismissed"
        label={includeDismissed ? "Showing dismissed" : "Hide dismissed"}
        tooltip="Toggle whether previously-dismissed events appear below."
        active={includeDismissed}
        onClick={onToggleDismissed}
      />
    </div>
  );
}
